/**
 * WebinarReplayPage — /webinars/:slug/replay
 *
 * The gated replay for a partner webinar. Same shape as the registration-event
 * replay (email gate → signed Bunny iframe → CTAs) because Michael's call on
 * 2026-07-28 was explicitly "the same replay functionality as an event" — but
 * webinar-flavoured: the hero carries the partner co-brand lockup, and the gate
 * matches against webinar registrants rather than assessment submissions.
 *
 * WHY A SIBLING PAGE RATHER THAN REUSING ReplayPage: ReplayPage scrapes its
 * brand shell out of an assessment's `config.intro.blocks` (first siteNav block,
 * last html block as footer) — a convention partner webinars have no equivalent
 * for; they use TakerShell plus the partner lockup. The genuinely intricate
 * part, watch-depth capture, IS shared via useWatchTracker so the two can never
 * drift on the numbers.
 *
 * A raw recording URL never reaches this page for a gated replay — the access
 * endpoint returns a signed, expiring Bunny iframe URL and nothing else.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import { fetchWithRetry } from './lib/fetchRetry';
import { useWatchTracker } from './lib/useWatchTracker';
import { useBrandGtm } from './lib/useBrandGtm';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface PartnerBlock { name: string; logoUrl: string | null }
interface ReplayPageCopy {
  eyebrow?: string; heading?: string; subheading?: string; lede?: string;
  closedHeading?: string; closedBody?: string;
  ctas?: { label: string; url: string; hint?: string }[];
}
type AccessResponse =
  | {
      ok: true; title: string;
      signedIframeUrl: string | null; externalUrl: string | null;
      durationSeconds: number | null;
      replayPage: ReplayPageCopy;
      partner: PartnerBlock | null;
      watchSession: { token: string } | null;
    }
  | { ok: false; reason: 'archived'; replayPage?: ReplayPageCopy }
  | { ok: false; reason: 'awaiting-replay' }
  | { ok: false; reason: 'not-registered' }
  | { ok: false; reason: 'no-source' };

/** Remember the unlocked email per webinar so a returning visitor isn't asked
 *  twice. Same key convention as WebinarPage's registration stamp. The value is
 *  only a convenience — the server re-checks registration on every access call,
 *  so a forged localStorage entry unlocks nothing. */
const emailKey = (slug: string) => `webinar-replay-email:${slug}`;

type State =
  | { phase: 'loading' }
  | { phase: 'gate'; error?: string }
  | { phase: 'unlocked'; data: Extract<AccessResponse, { ok: true }> }
  | { phase: 'closed'; copy: ReplayPageCopy }
  | { phase: 'pending' }
  | { phase: 'error'; message: string };

export default function WebinarReplayPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const token = params.get('t');
  const [state, setState] = useState<State>({ phase: 'loading' });
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [brandGtmId, setBrandGtmId] = useState<string | undefined>(undefined);
  const [brandSettled, setBrandSettled] = useState(false);

  // Brand payload drives GTM. Gated on settled so a non-TSB brand is never
  // preceded by the TSB fallback container (double-counted pageviews).
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    fetchWithRetry(`${API_BASE}/api/public/webinars/${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setBrandGtmId(d?.webinar?.brand?.gtmContainerId ?? undefined); })
      .catch(() => { /* branding is decorative here — never block the player */ })
      .finally(() => { if (alive) setBrandSettled(true); });
    return () => { alive = false; };
  }, [slug]);
  useBrandGtm(brandGtmId, brandSettled);

  /** Ask the server whether this viewer may watch. NEVER fetchWithRetry — this
   *  is a POST, and the repo's rule is that retrying a mutating request risks
   *  double-writing (here: duplicate replay_views rows / inflated view counts). */
  const requestAccess = useCallback(async (body: { token?: string; email?: string }) => {
    const res = await fetch(
      `${API_BASE}/api/public/webinars/${encodeURIComponent(slug)}/replay/access`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
    );
    if (!res.ok) throw new Error(`Could not load the replay (${res.status})`);
    return (await res.json()) as AccessResponse;
  }, [slug]);

  const applyResult = useCallback((d: AccessResponse, typedEmail?: string) => {
    if (d.ok) {
      if (typedEmail) {
        try { window.localStorage.setItem(emailKey(slug), typedEmail); } catch { /* private mode */ }
      }
      setState({ phase: 'unlocked', data: d });
      return;
    }
    if (d.reason === 'archived') { setState({ phase: 'closed', copy: d.replayPage ?? {} }); return; }
    if (d.reason === 'awaiting-replay' || d.reason === 'no-source') { setState({ phase: 'pending' }); return; }
    setState({ phase: 'gate', error: "We couldn't find that email on the registration list for this webinar." });
  }, [slug]);

  // First load: a token in the link unlocks directly; otherwise try a
  // remembered email; otherwise show the gate.
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    let remembered: string | null = null;
    try { remembered = window.localStorage.getItem(emailKey(slug)); } catch { remembered = null; }

    // Always probe, even with no credentials to offer. The server checks the
    // replay PHASE before it checks identity, so an empty request still tells
    // us "not published yet" or "closed" — and a visitor who arrives early
    // deserves to hear that instead of being asked to type an email that was
    // never going to open anything. Only a genuinely open replay falls through
    // to 'not-registered', which is the real cue to show the gate.
    requestAccess(token ? { token } : remembered ? { email: remembered } : {})
      .then((d) => {
        if (!alive) return;
        // 'not-registered' here means the replay IS open and we simply don't
        // know who this is yet — that's the gate, not an error. Only surface
        // the "we couldn't find that email" message when they actually typed one.
        if (!d.ok && d.reason === 'not-registered' && !token) {
          setState(remembered
            ? { phase: 'gate', error: "That email isn't on the registration list for this webinar." }
            : { phase: 'gate' });
          return;
        }
        applyResult(d);
      })
      .catch((e: Error) => { if (alive) setState({ phase: 'error', message: e.message }); });
    return () => { alive = false; };
  }, [slug, token, requestAccess, applyResult]);

  async function submitGate(e: FormEvent) {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      setState({ phase: 'gate', error: 'Enter a valid email address.' });
      return;
    }
    setBusy(true);
    try {
      applyResult(await requestAccess({ email: value }), value);
    } catch (err) {
      setState({ phase: 'error', message: err instanceof Error ? err.message : 'Something went wrong.' });
    } finally {
      setBusy(false);
    }
  }

  const partner = state.phase === 'unlocked' ? state.data.partner : null;
  const shellFooter = partner ? `In partnership with ${partner.name}` : undefined;

  return (
    <TakerShell footerLabel={shellFooter}>
      {/* minHeight keeps the dark hero filling the viewport below the nav —
          without it a short state (the gate, or "on its way") leaves a band of
          dead white page under the fold. */}
      <section style={{
        background: 'linear-gradient(160deg,#0A0A2E 0%,#141438 100%)', color: '#fff',
        padding: '48px 24px 56px', minHeight: 'calc(100vh - 190px)',
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {partner && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 26, flexWrap: 'wrap' }}>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: 600, letterSpacing: 0.3 }}>
                In partnership with
              </span>
              {partner.logoUrl
                ? <img src={partner.logoUrl} alt={partner.name} style={{ height: 52, width: 'auto', borderRadius: 5 }} />
                : <span style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{partner.name}</span>}
            </div>
          )}
          <Body
            state={state}
            email={email}
            setEmail={setEmail}
            busy={busy}
            onSubmit={submitGate}
            slug={slug}
          />
        </div>
      </section>
    </TakerShell>
  );
}

function Body({ state, email, setEmail, busy, onSubmit, slug }: {
  state: State; email: string; setEmail: (v: string) => void;
  busy: boolean; onSubmit: (e: FormEvent) => void; slug: string;
}) {
  if (state.phase === 'loading') {
    return <p style={{ color: 'rgba(255,255,255,0.7)' }}>Loading the replay…</p>;
  }

  if (state.phase === 'error') {
    return (
      <>
        <h1 style={h1Style}>We couldn't load the replay</h1>
        <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: 22 }}>{state.message}</p>
        <button onClick={() => window.location.reload()} style={primaryBtn}>Try again</button>
      </>
    );
  }

  if (state.phase === 'pending') {
    return (
      <>
        <h1 style={h1Style}>The replay is on its way</h1>
        <p style={{ color: 'rgba(255,255,255,0.85)', maxWidth: 560 }}>
          We're still getting the recording ready. Everyone who registered will get an email
          the moment it's available — nothing more for you to do.
        </p>
        <a href={`/webinars/${encodeURIComponent(slug)}`} style={{ ...secondaryLink, marginTop: 22, display: 'inline-block' }}>
          Back to the webinar page
        </a>
      </>
    );
  }

  if (state.phase === 'closed') {
    const c = state.copy;
    return (
      <>
        <h1 style={h1Style}>{c.closedHeading ?? 'This replay has closed'}</h1>
        <p style={{ color: 'rgba(255,255,255,0.85)', maxWidth: 560, marginBottom: 24 }}>
          {c.closedBody ?? 'The viewing window for this recording has ended. Have a look at what else is on — there is usually something coming up.'}
        </p>
        <CtaRow ctas={c.ctas} />
      </>
    );
  }

  if (state.phase === 'gate') {
    return (
      <>
        <h1 style={h1Style}>Watch the replay</h1>
        <p style={{ color: 'rgba(255,255,255,0.85)', maxWidth: 520, marginBottom: 24 }}>
          Enter the email you registered with and we'll bring up the recording.
        </p>
        <form onSubmit={onSubmit} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', maxWidth: 520 }}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourfirm.com"
            autoComplete="email"
            style={{
              flex: '1 1 260px', padding: '12px 14px', borderRadius: 8, fontSize: 15,
              border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', color: '#fff',
            }}
          />
          <button type="submit" disabled={busy} style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Checking…' : 'Watch the replay'}
          </button>
        </form>
        {state.error && (
          <p style={{ color: '#FCA5A5', marginTop: 14, maxWidth: 520 }}>
            {state.error}{' '}
            <a href={`/webinars/${encodeURIComponent(slug)}`} style={secondaryLink}>
              Register for the next one →
            </a>
          </p>
        )}
      </>
    );
  }

  return <Player data={state.data} slug={slug} />;
}

function Player({ data, slug }: { data: Extract<AccessResponse, { ok: true }>; slug: string }) {
  const rp = data.replayPage ?? {};
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  // Shared with the registration-event replay — one watch-depth implementation.
  useWatchTracker(iframeRef, {
    heartbeatUrl: `${API_BASE}/api/public/webinars/${encodeURIComponent(slug)}/replay/heartbeat`,
    token: data.watchSession?.token ?? null,
    hasIframe: !!data.signedIframeUrl,
  });

  return (
    <>
      {rp.eyebrow && (
        <div style={{ fontSize: 13, fontWeight: 700, color: '#F05A28', letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 12 }}>
          {rp.eyebrow}
        </div>
      )}
      <h1 style={h1Style}>{rp.heading ?? `${data.title} · Replay`}</h1>
      {rp.subheading && (
        <p style={{ color: 'rgba(255,255,255,0.85)', maxWidth: 620, marginBottom: 22 }}>{rp.subheading}</p>
      )}

      {data.signedIframeUrl ? (
        <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 12, overflow: 'hidden', marginBottom: 28 }}>
          <iframe
            ref={iframeRef}
            src={data.signedIframeUrl}
            loading="lazy"
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
            title={`${data.title} replay`}
          />
        </div>
      ) : data.externalUrl ? (
        // Partner-hosted recording — deliberately a link, not an embed: we
        // don't control that player and can't measure watch depth in it.
        <a href={data.externalUrl} target="_blank" rel="noopener noreferrer" style={{ ...primaryBtn, display: 'inline-block', marginBottom: 28 }}>
          Watch the replay (opens in a new tab) →
        </a>
      ) : null}

      <CtaRow ctas={rp.ctas} />
    </>
  );
}

function CtaRow({ ctas }: { ctas?: { label: string; url: string; hint?: string }[] }) {
  if (!ctas?.length) return null;
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8 }}>
      {ctas.map((c, i) => (
        <a
          key={`${c.url}-${i}`}
          href={c.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: '1 1 260px', padding: '16px 20px', borderRadius: 12, textDecoration: 'none',
            background: i === 0 ? '#F05A28' : 'rgba(255,255,255,0.08)',
            border: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.2)',
            color: '#fff',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 15 }}>{c.label}</div>
          {c.hint && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginTop: 4 }}>{c.hint}</div>}
        </a>
      ))}
    </div>
  );
}

const h1Style: React.CSSProperties = {
  fontFamily: "'Poppins', sans-serif", fontSize: 36, lineHeight: 1.12,
  fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 16,
};
const primaryBtn: React.CSSProperties = {
  padding: '12px 22px', borderRadius: 8, border: 'none', background: '#F05A28',
  color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer',
};
const secondaryLink: React.CSSProperties = { color: '#FDBA74', fontWeight: 600 };
